/**
 * UAT Question Import Utility
 * 
 * Run this script to import all UAT questions and templates into the question bank.
 * Can be called from the admin interface or via a one-time setup script.
 * 
 * Supports both CREATE and UPDATE modes:
 * - CREATE: Only creates new items, skips existing ones
 * - UPDATE: Updates existing items with new content, creates missing ones
 */

import questionBankService, { TemplateCreate, TemplateQuestionCreate, QuestionUpdate, TemplateUpdate } from '@/services/api/questionBankService'
import { ALL_UAT_QUESTIONS } from './uatQuestions'
import { ALL_UAT_TEMPLATES } from './uatTemplates'

interface ImportResult {
  questionsCreated: number
  questionsUpdated: number
  questionsFailed: number
  templatesCreated: number
  templatesUpdated: number
  templatesFailed: number
  errors: string[]
}

// Map of question_code -> database UUID (populated after questions are created/fetched)
let questionCodeToIdMap: Map<string, string> = new Map()

// Map of template_code -> database UUID
let templateCodeToIdMap: Map<string, string> = new Map()

/**
 * Fetch all UAT questions and build code-to-ID mapping
 */
async function buildQuestionCodeMap(): Promise<void> {
  try {
    // Fetch all questions with UAT codes
    const response = await questionBankService.listQuestions({ 
      search: 'UAT-', 
      limit: 100,
      is_active: true
    })
    
    const questions = response?.items || []
    questionCodeToIdMap = new Map()
    
    for (const q of questions) {
      if (q.question_code.startsWith('UAT-')) {
        questionCodeToIdMap.set(q.question_code, q.id)
      }
    }
    
    if (import.meta.env.DEV) console.log(`📍 Mapped ${questionCodeToIdMap.size} UAT question codes to IDs`)
  } catch (error) {
    console.error('Failed to fetch questions for mapping:', error)
  }
}

/**
 * Fetch all UAT templates and build code-to-ID mapping
 */
async function buildTemplateCodeMap(): Promise<void> {
  try {
    const response = await questionBankService.listTemplates({ 
      search: 'UAT-', 
      limit: 50,
      is_active: true
    })
    
    const templates = response?.items || []
    templateCodeToIdMap = new Map()
    
    for (const t of templates) {
      if (t.template_code.startsWith('UAT-')) {
        templateCodeToIdMap.set(t.template_code, t.id)
      }
    }
    
    if (import.meta.env.DEV) console.log(`📍 Mapped ${templateCodeToIdMap.size} UAT template codes to IDs`)
  } catch (error) {
    console.error('Failed to fetch templates for mapping:', error)
  }
}

/**
 * Import all UAT questions into the question bank
 * @param updateExisting - If true, updates existing questions; if false, skips them
 */
export async function importUATQuestions(updateExisting = false): Promise<{ created: number; updated: number; failed: number; errors: string[] }> {
  const results = { created: 0, updated: 0, failed: 0, errors: [] as string[] }
  
  // Build mapping first if we need to update
  if (updateExisting) {
    await buildQuestionCodeMap()
  }
  
  for (const question of ALL_UAT_QUESTIONS) {
    try {
      // The shared `QuestionCreate` shape now has `question_code` as
      // optional (the backend auto-generates one on create), but every
      // hard-coded UAT seed entry sets it. Coerce to the empty string
      // for the lookup; entries without a code wouldn't match anything
      // pre-existing anyway.
      const existingId = questionCodeToIdMap.get(question.question_code || '')
      
      if (existingId && updateExisting) {
        // Update existing question
        const updateData: QuestionUpdate = {
          question_text: question.question_text,
          question_type: question.question_type,
          category: question.category,
          question_config: question.question_config,
          applicable_feedback_contexts: question.applicable_feedback_contexts,
          is_required: question.is_required,
          display_order: question.display_order,
          helper_text: question.helper_text,
        }
        await questionBankService.updateQuestion(existingId, updateData)
        results.updated++
        if (import.meta.env.DEV) console.log(`↻ Updated question: ${question.question_code}`)
      } else if (!existingId) {
        // Create new question
        await questionBankService.createQuestion(question)
        results.created++
        if (import.meta.env.DEV) console.log(`✓ Created question: ${question.question_code}`)
      } else {
        if (import.meta.env.DEV) console.log(`⊘ Skipped (exists): ${question.question_code}`)
      }
    } catch (error: any) {
      // Check if it's a duplicate error (already exists) - only relevant in create mode
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        if (import.meta.env.DEV) console.log(`⊘ Skipped (exists): ${question.question_code}`)
      } else {
        results.failed++
        const errorMsg = `✗ Failed: ${question.question_code} - ${error.response?.data?.detail || error.message}`
        results.errors.push(errorMsg)
        console.error(errorMsg)
      }
    }
  }
  
  return results
}

/**
 * Transform template questions to use actual UUIDs instead of question codes
 */
function transformTemplateQuestions(questions: TemplateQuestionCreate[] | undefined): TemplateQuestionCreate[] {
  if (!questions) return []
  
  return questions.map(tq => {
    // The question_id in our template data is actually the question_code
    const questionCode = tq.question_id
    const actualId = questionCodeToIdMap.get(questionCode)
    
    if (!actualId) {
      console.warn(`⚠️ No ID found for question code: ${questionCode}`)
      return tq // Return as-is, will likely fail
    }
    
    return {
      ...tq,
      question_id: actualId
    }
  })
}

/**
 * Import all UAT templates into the question bank
 * Note: Questions must be imported first so templates can reference them
 * @param updateExisting - If true, updates existing templates; if false, skips them
 */
export async function importUATTemplates(updateExisting = false): Promise<{ created: number; updated: number; failed: number; errors: string[] }> {
  const results = { created: 0, updated: 0, failed: 0, errors: [] as string[] }
  
  // Build the question code -> ID mapping first
  await buildQuestionCodeMap()
  
  if (questionCodeToIdMap.size === 0) {
    console.error('❌ No UAT questions found in database. Import questions first!')
    results.errors.push('No UAT questions found - import questions first')
    return results
  }
  
  // Build template mapping if we need to update
  if (updateExisting) {
    await buildTemplateCodeMap()
  }
  
  for (const template of ALL_UAT_TEMPLATES) {
    try {
      // Transform question references from codes to UUIDs
      const transformedTemplate: TemplateCreate = {
        ...template,
        questions: transformTemplateQuestions(template.questions)
      }
      
      const existingId = templateCodeToIdMap.get(template.template_code)
      
      if (existingId && updateExisting) {
        // Update existing template
        const updateData: TemplateUpdate = {
          template_name: template.template_name,
          description: template.description,
          feedback_context: template.feedback_context,
          primary_stakeholder_role: template.primary_stakeholder_role,
          estimated_completion_time: template.estimated_completion_time,
          allow_custom_question: template.allow_custom_question,
          questions: transformedTemplate.questions,
        }
        await questionBankService.updateTemplate(existingId, updateData)
        results.updated++
        if (import.meta.env.DEV) console.log(`↻ Updated template: ${template.template_code}`)
      } else if (!existingId) {
        // Create new template
        await questionBankService.createTemplate(transformedTemplate)
        results.created++
        if (import.meta.env.DEV) console.log(`✓ Created template: ${template.template_code}`)
      } else {
        if (import.meta.env.DEV) console.log(`⊘ Skipped (exists): ${template.template_code}`)
      }
    } catch (error: any) {
      // Check if it's a duplicate error (already exists) - only relevant in create mode
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        if (import.meta.env.DEV) console.log(`⊘ Skipped (exists): ${template.template_code}`)
      } else {
        results.failed++
        const errorMsg = `✗ Failed: ${template.template_code} - ${error.response?.data?.detail || error.message}`
        results.errors.push(errorMsg)
        console.error(errorMsg)
      }
    }
  }
  
  return results
}

/**
 * Import all UAT questions and templates
 * @param updateExisting - If true, updates existing items with new content
 */
export async function importAllUATData(updateExisting = false): Promise<ImportResult> {
  const mode = updateExisting ? 'UPDATE' : 'CREATE'
  if (import.meta.env.DEV) console.log(`🚀 Starting UAT data import (${mode} mode)...\n`)
  
  // Step 1: Import questions first
  if (import.meta.env.DEV) console.log('📝 Importing UAT Questions...')
  const questionResults = await importUATQuestions(updateExisting)
  if (import.meta.env.DEV) console.log(`   Questions: ${questionResults.created} created, ${questionResults.updated} updated, ${questionResults.failed} failed\n`)
  
  // Step 2: Import templates (which reference questions)
  if (import.meta.env.DEV) console.log('📋 Importing UAT Templates...')
  const templateResults = await importUATTemplates(updateExisting)
  if (import.meta.env.DEV) console.log(`   Templates: ${templateResults.created} created, ${templateResults.updated} updated, ${templateResults.failed} failed\n`)
  
  const result: ImportResult = {
    questionsCreated: questionResults.created,
    questionsUpdated: questionResults.updated,
    questionsFailed: questionResults.failed,
    templatesCreated: templateResults.created,
    templatesUpdated: templateResults.updated,
    templatesFailed: templateResults.failed,
    errors: [...questionResults.errors, ...templateResults.errors]
  }
  
  if (import.meta.env.DEV) console.log('✅ UAT data import complete!')
  console.log(`   Total questions: ${result.questionsCreated} created, ${result.questionsUpdated} updated`)
  if (import.meta.env.DEV) console.log(`   Total templates: ${result.templatesCreated} created, ${result.templatesUpdated} updated`)
  
  if (result.errors.length > 0) {
    if (import.meta.env.DEV) console.log(`\n⚠️ Errors (${result.errors.length}):`)
    result.errors.forEach(e => console.log(`   ${e}`))
  }
  
  return result
}

export default importAllUATData
